/**
 * Datadog RUM (Real User Monitoring) Utilities
 *
 * Type-safe wrapper functions for the Datadog RUM API with graceful fallback handling.
 * These utilities assume DD RUM is initialized by the consuming application.
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Extend the global Window interface to include DD_RUM
 */
declare global {
  interface Window {
    DD_RUM?: {
      addAction: (name: string, context?: Record<string, unknown>) => void;
      addError: (
        error: Error | string,
        context?: Record<string, unknown>,
      ) => void;
      addTiming: (name: string, time?: number) => void;
      setGlobalContextProperty: (key: string, value: unknown) => void;
      removeGlobalContextProperty: (key: string) => void;
    } | null;
  }
}

/**
 * Performance metrics for RUM tracking
 */
export type PerformanceMetrics = {
  jankCount?: number;
  maxJankDuration?: number;
  totalJankTime?: number;
  inp?: number;
  cls?: number;
};

/**
 * Layout shift entry type for CLS tracking
 * Based on the LayoutShift interface from the Layout Instability API spec
 * @see https://wicg.github.io/layout-instability/#layoutshift
 */
type LayoutShiftEntry = PerformanceEntry & {
  hadRecentInput: boolean;
  value: number;
};

/**
 * Callback function type for performance observers
 */
export type PerformanceObserverCallback = (metrics: PerformanceMetrics) => void;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if Datadog RUM is available on the window object
 *
 * @returns {boolean} True if DD_RUM is initialized, false otherwise
 */
export const isDatadogRumAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.DD_RUM !== 'undefined' &&
    window.DD_RUM !== null
  );
};

/**
 * Safely add a RUM action
 *
 * Tracks user actions with optional context. If DD RUM is not available,
 * logs a warning to the console but does not throw an error.
 *
 * @param {string} name - The action name (e.g., 'drawer:opened')
 * @param {Record<string, unknown>} [context] - Optional context object
 *
 * @example
 * addDatadogAction('drawer:opened', { componentId: 'my-drawer', size: 'lg' });
 */
export const addDatadogAction = (
  name: string,
  context?: Record<string, unknown>,
): void => {
  if (!isDatadogRumAvailable()) {
    console.warn(`[Datadog RUM] Not initialized. Skipping action: ${name}`);
    return;
  }

  try {
    window.DD_RUM?.addAction(name, context);
  } catch (error) {
    console.error('[Datadog RUM] Error adding action:', error);
  }
};

/**
 * Safely add a RUM error
 *
 * Tracks errors with optional context. If DD RUM is not available,
 * logs a warning to the console but does not throw an error.
 *
 * @param {Error | string} error - The error to track
 * @param {Record<string, unknown>} [context] - Optional context object
 *
 * @example
 * addDatadogError(new Error('Failed to load data'), { componentId: 'drawer' });
 */
export const addDatadogError = (
  error: Error | string,
  context?: Record<string, unknown>,
): void => {
  if (!isDatadogRumAvailable()) {
    const errorMessage = error instanceof Error ? error.message : error;
    console.warn(
      `[Datadog RUM] Not initialized. Skipping error: ${errorMessage}`,
    );
    return;
  }

  try {
    window.DD_RUM?.addError(error, context);
  } catch (err) {
    console.error('[Datadog RUM] Error adding error:', err);
  }
};

/**
 * Safely add a RUM timing
 *
 * Tracks performance timings. If DD RUM is not available,
 * logs a warning to the console but does not throw an error.
 *
 * @param {string} name - The timing name (e.g., 'drawer:qdrawer:open')
 * @param {number} [time] - Optional timestamp (defaults to current time)
 *
 * @example
 * addDatadogTiming('drawer:qdrawer:open');
 */
export const addDatadogTiming = (name: string, time?: number): void => {
  if (!isDatadogRumAvailable()) {
    console.warn(`[Datadog RUM] Not initialized. Skipping timing: ${name}`);
    return;
  }

  try {
    window.DD_RUM?.addTiming(name, time);
  } catch (error) {
    console.error('[Datadog RUM] Error adding timing:', error);
  }
};

// ============================================================================
// Performance Monitoring Utilities
// ============================================================================

/**
 * Check if PerformanceObserver API is supported
 *
 * @returns {boolean} True if PerformanceObserver is available, false otherwise
 */
export const isPerformanceObserverSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.PerformanceObserver !== 'undefined'
  );
};

/**
 * Create a jank observer to detect long tasks (frames > threshold)
 *
 * Monitors longtask entries to identify rendering bottlenecks that impact user experience.
 * "Jank" refers to frames that take longer than expected (default: 50ms for 60fps).
 *
 * @param {function} callback - Called when jank is detected with accumulated metrics
 * @param {number} [threshold=50] - Minimum duration in ms to consider as jank
 * @returns {PerformanceObserver | null} Observer instance or null if not supported
 *
 * @example
 * const observer = createJankObserver((metrics) => {
 *   console.log(`Detected ${metrics.jankCount} jank events`);
 * });
 */
export const createJankObserver = (
  callback: (
    jankMetrics: Pick<
      PerformanceMetrics,
      'jankCount' | 'maxJankDuration' | 'totalJankTime'
    >,
  ) => void,
  threshold = 50,
): PerformanceObserver | null => {
  if (!isPerformanceObserverSupported()) {
    console.warn(
      '[Performance] PerformanceObserver not supported. Skipping jank tracking.',
    );
    return null;
  }

  try {
    let jankCount = 0;
    let maxJankDuration = 0;
    let totalJankTime = 0;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();

      entries.forEach((entry) => {
        if (entry.entryType === 'longtask' && entry.duration > threshold) {
          jankCount++;
          totalJankTime += entry.duration;
          maxJankDuration = Math.max(maxJankDuration, entry.duration);
        }
      });

      if (jankCount > 0) {
        callback({ jankCount, maxJankDuration, totalJankTime });
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
    return observer;
  } catch (error) {
    console.error('[Performance] Error creating jank observer:', error);
    return null;
  }
};

/**
 * Measure INP (Interaction to Next Paint)
 *
 * Measures the time from an interaction (e.g., button click) to when the visual
 * feedback is painted on screen. This is a key Web Vitals metric for responsiveness.
 *
 * @param {string} interactionName - Name of the interaction being measured
 * @param {function} callback - Called with the measured INP value in milliseconds
 * @returns {function} Finish function to call when interaction is complete
 *
 * @example
 * const finishINP = measureINP('drawer:open', (inp) => {
 *   console.log(`Drawer opened in ${inp}ms`);
 * });
 * // ... perform interaction ...
 * finishINP();
 */
export const measureINP = (
  interactionName: string,
  callback: (inp: number) => void,
): (() => void) => {
  const startTime = performance.now();

  return () => {
    requestAnimationFrame(() => {
      const inp = performance.now() - startTime;
      callback(inp);
    });
  };
};

/**
 * Create a CLS (Cumulative Layout Shift) observer
 *
 * Tracks unexpected layout shifts that occur during the page lifecycle.
 * CLS is a key Web Vitals metric measuring visual stability.
 *
 * @param {function} callback - Called with updated CLS value on each layout shift
 * @returns {PerformanceObserver | null} Observer instance or null if not supported
 *
 * @example
 * const observer = createCLSObserver((cls) => {
 *   console.log(`Current CLS: ${cls}`);
 * });
 */
export const createCLSObserver = (
  callback: (cls: number) => void,
): PerformanceObserver | null => {
  if (!isPerformanceObserverSupported()) {
    console.warn(
      '[Performance] PerformanceObserver not supported. Skipping CLS tracking.',
    );
    return null;
  }

  try {
    let clsValue = 0;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();

      entries.forEach((entry) => {
        // Only count layout shifts that weren't caused by user input
        if (entry.entryType === 'layout-shift') {
          const layoutShiftEntry = entry as LayoutShiftEntry;
          if (!layoutShiftEntry.hadRecentInput) {
            clsValue += layoutShiftEntry.value;
            callback(clsValue);
          }
        }
      });
    });

    observer.observe({ entryTypes: ['layout-shift'] });
    return observer;
  } catch (error) {
    console.error('[Performance] Error creating CLS observer:', error);
    return null;
  }
};
